/**
 * Modal Persistence Hook
 * 
 * Provides persistent modal state that survives component remounting.
 * Stores modal state in Redux to prevent closure during WebSocket updates.
 */

import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createSlice } from '@reduxjs/toolkit';

// Modal persistence Redux slice
const modalPersistenceSlice = createSlice({
  name: 'modalPersistence',
  initialState: {
    modals: {}, // { modalId: { isOpen, data, timestamp, componentId } }
    globalModalState: {
      preventClose: false,
      lastUpdate: null
    }
  },
  reducers: {
    setModalState: (state, action) => {
      const { modalId, isOpen, data, componentId } = action.payload;
      
      state.modals[modalId] = {
        isOpen: Boolean(isOpen),
        data: data || null,
        timestamp: Date.now(),
        componentId: componentId || null,
        lastUpdate: Date.now()
      };
      
      state.globalModalState.lastUpdate = Date.now();
    },
    
    closeModal: (state, action) => {
      const { modalId } = action.payload;
      
      if (state.modals[modalId]) {
        state.modals[modalId].isOpen = false;
        state.modals[modalId].lastUpdate = Date.now();
      }
      
      state.globalModalState.lastUpdate = Date.now();
    },
    
    closeAllModals: (state) => {
      Object.keys(state.modals).forEach(modalId => {
        state.modals[modalId].isOpen = false;
        state.modals[modalId].lastUpdate = Date.now();
      });
      
      state.globalModalState.lastUpdate = Date.now();
    },
    
    setPreventClose: (state, action) => {
      state.globalModalState.preventClose = Boolean(action.payload);
      state.globalModalState.lastUpdate = Date.now();
    },
    
    removeModal: (state, action) => {
      const { modalId } = action.payload;
      delete state.modals[modalId];
      state.globalModalState.lastUpdate = Date.now();
    },
    
    cleanupOldModals: (state, action) => {
      const { maxAge = 3600000 } = action.payload; // Default 1 hour
      const cutoff = Date.now() - maxAge;
      
      Object.keys(state.modals).forEach(modalId => {
        if (state.modals[modalId].timestamp < cutoff && !state.modals[modalId].isOpen) {
          delete state.modals[modalId];
        }
      });
      
      state.globalModalState.lastUpdate = Date.now();
    }
  }
});

export const {
  setModalState,
  closeModal,
  closeAllModals,
  setPreventClose,
  removeModal,
  cleanupOldModals
} = modalPersistenceSlice.actions;

export const modalPersistenceReducer = modalPersistenceSlice.reducer;

// Selectors
const selectModalState = (state, modalId) => state.modalPersistence?.modals[modalId] || null;
const selectGlobalModalState = (state) => state.modalPersistence?.globalModalState || {};
const selectAllModals = (state) => state.modalPersistence?.modals || {};

/**
 * Main modal persistence hook
 */
export const useModalPersistence = (modalId, options = {}) => {
  const {
    autoCleanup = true,
    maxAge = 3600000, // 1 hour
    preventCloseOnUpdate = true,
    componentId = null
  } = options;

  const dispatch = useDispatch();
  const modalState = useSelector(state => selectModalState(state, modalId));
  const globalState = useSelector(selectGlobalModalState);
  const componentIdRef = useRef(componentId || `component_${Date.now()}_${Math.random()}`);
  const mountedRef = useRef(true);

  // Track if modal was open before component remount
  const wasOpenRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    
    // If modal state exists and was open, it survived a remount
    if (modalState?.isOpen) {
      wasOpenRef.current = true;
      console.log(`🔄 Modal ${modalId} survived component remount`);
    }

    return () => {
      mountedRef.current = false;
    };
  }, [modalId, modalState?.isOpen]);

  // Auto cleanup old modals
  useEffect(() => {
    if (autoCleanup) {
      const cleanupInterval = setInterval(() => {
        dispatch(cleanupOldModals({ maxAge }));
      }, 5 * 60 * 1000); // Every 5 minutes

      return () => clearInterval(cleanupInterval);
    }
  }, [dispatch, autoCleanup, maxAge]);

  /**
   * Open modal with data
   */
  const openModal = useCallback((data = null) => {
    if (!mountedRef.current) return;

    console.log(`🔄 Opening persistent modal: ${modalId}`);
    
    dispatch(setModalState({
      modalId,
      isOpen: true,
      data,
      componentId: componentIdRef.current
    }));
  }, [dispatch, modalId]);

  /**
   * Close modal
   */
  const closeModalHandler = useCallback((force = false) => {
    if (!mountedRef.current && !force) return;

    // Check if close is prevented
    if (preventCloseOnUpdate && globalState.preventClose && !force) {
      console.log(`⚠️ Modal close prevented for ${modalId} due to global prevent close`);
      return;
    }

    console.log(`🔄 Closing persistent modal: ${modalId}`);
    
    dispatch(closeModal({ modalId }));
  }, [dispatch, modalId, globalState.preventClose, preventCloseOnUpdate]);

  /**
   * Toggle modal state
   */
  const toggleModal = useCallback((data = null) => {
    if (modalState?.isOpen) {
      closeModalHandler();
    } else {
      openModal(data);
    }
  }, [modalState?.isOpen, openModal, closeModalHandler]);

  /**
   * Update modal data without changing open state
   */
  const updateModalData = useCallback((data) => {
    if (!mountedRef.current) return;
    
    if (modalState?.isOpen) {
      dispatch(setModalState({
        modalId,
        isOpen: true,
        data,
        componentId: componentIdRef.current
      }));
    }
  }, [dispatch, modalId, modalState?.isOpen]);

  /**
   * Set prevent close globally
   */
  const setPreventCloseGlobal = useCallback((prevent) => {
    dispatch(setPreventClose(prevent));
  }, [dispatch]);

  /**
   * Remove modal from persistence (cleanup)
   */
  const removeModalFromPersistence = useCallback(() => {
    dispatch(removeModal({ modalId }));
  }, [dispatch, modalId]);

  // Return modal state and controls
  return {
    // State
    isOpen: modalState?.isOpen || false,
    data: modalState?.data || null,
    timestamp: modalState?.timestamp || null,
    lastUpdate: modalState?.lastUpdate || null,
    wasOpenBeforeRemount: wasOpenRef.current,
    
    // Controls
    openModal,
    closeModal: closeModalHandler,
    toggleModal,
    updateModalData,
    
    // Global controls
    setPreventCloseGlobal,
    isClosePreventedGlobally: globalState.preventClose,
    
    // Cleanup
    removeModalFromPersistence,
    
    // Metadata
    componentId: componentIdRef.current,
    modalId,
    
    // Helper methods
    forceClose: () => closeModalHandler(true),
    isOwnedByCurrentComponent: modalState?.componentId === componentIdRef.current
  };
};

/**
 * Hook for managing multiple modals
 */
export const useMultiModalPersistence = (modalIds, options = {}) => {
  const allModals = useSelector(selectAllModals);
  const dispatch = useDispatch();

  const modals = modalIds.reduce((acc, modalId) => {
    acc[modalId] = useModalPersistence(modalId, options);
    return acc;
  }, {});

  const closeAllModalsHandler = useCallback(() => {
    dispatch(closeAllModals());
  }, [dispatch]);

  const getOpenModals = useCallback(() => {
    return Object.entries(allModals)
      .filter(([_, state]) => state.isOpen)
      .map(([modalId, state]) => ({ modalId, ...state }));
  }, [allModals]);

  const hasOpenModals = useCallback(() => {
    return Object.values(allModals).some(state => state.isOpen);
  }, [allModals]);

  return {
    modals,
    closeAllModals: closeAllModalsHandler,
    getOpenModals,
    hasOpenModals: hasOpenModals(),
    openModalCount: Object.values(allModals).filter(state => state.isOpen).length
  };
};

/**
 * Hook for modal state monitoring (debugging)
 */
export const useModalStateMonitor = () => {
  const allModals = useSelector(selectAllModals);
  const globalState = useSelector(selectGlobalModalState);

  return {
    allModals,
    globalState,
    totalModals: Object.keys(allModals).length,
    openModals: Object.values(allModals).filter(state => state.isOpen).length,
    oldModals: Object.values(allModals).filter(
      state => Date.now() - state.timestamp > 3600000 // Older than 1 hour
    ).length
  };
};

export default useModalPersistence;
